import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Box,
  Typography,
  Button,
  Container,
  Stack,
  useTheme,
  alpha,
} from "@mui/material";
import {
  SentimentVeryDissatisfied as SadIcon,
  Home as HomeIcon,
  Explore as ExploreIcon,
} from "@mui/icons-material";

export default function NotFound() {
  const theme = useTheme();

  const floatingAnimation = {
    y: ["0%", "-15%", "0%"],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: "easeInOut",
    },
  };

  const glitchAnimation = {
    x: [0, -3, 3, -2, 2, -1, 1, 0],
    transition: {
      duration: 0.15,
      repeat: Infinity,
      repeatDelay: 4,
    },
  };

  const rotateAnimation = {
    rotate: [0, 360],
    transition: {
      duration: 20,
      repeat: Infinity,
      ease: "linear",
    },
  };

  return (
    <Box
      sx={{
        minHeight: "100svh",
        background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 50%, #071a2b 100%)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Animated gradient orbs */}
      <Box
        sx={{
          position: "absolute",
          top: "10%",
          left: "-10%",
          width: "500px",
          height: "500px",
          borderRadius: "50%",
          background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.15)} 0%, transparent 70%)`,
          animation: "float 8s ease-in-out infinite",
          "@keyframes float": {
            "0%, 100%": { transform: "translate(0, 0)" },
            "50%": { transform: "translate(50px, 50px)" },
          },
        }}
      />

      <Box
        sx={{
          position: "absolute",
          bottom: "10%",
          right: "-10%",
          width: "600px",
          height: "600px",
          borderRadius: "50%",
          background: `radial-gradient(circle, ${alpha(theme.palette.secondary.main, 0.1)} 0%, transparent 70%)`,
          animation: "floatReverse 10s ease-in-out infinite",
          "@keyframes floatReverse": {
            "0%, 100%": { transform: "translate(0, 0)" },
            "50%": { transform: "translate(-50px, -30px)" },
          },
        }}
      />

      <Container maxWidth="md" sx={{ height: "100%" }}>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, type: "spring", stiffness: 100 }}
        >
          <Stack alignItems="center" textAlign="center">
            {/* Animated 404 number */}
            <motion.div
              animate={floatingAnimation}
              style={{ position: "relative" }}
            >
              <Typography
                variant="h1"
                sx={{
                  fontSize: {
                    xs: "100px",
                    sm: "140px",
                    md: "180px",
                    lg: "220px",
                  },
                  fontWeight: 900,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main}, ${theme.palette.primary.light})`,
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  color: "transparent",
                  textShadow: `0 0 40px ${alpha(theme.palette.primary.main, 0.3)}`,
                  letterSpacing: "20px",
                }}
              >
                4
                <motion.span
                  animate={glitchAnimation}
                  style={{ display: "inline-block", letterSpacing: "0" }}
                >
                  0
                </motion.span>
                4
              </Typography>
            </motion.div>

            {/* Animated sad icon with orbit effect */}
            <Box
              sx={{
                position: "relative",
                width: "100px",
                height: "100px",
                mx: "auto",
              }}
            >
              <motion.div
                animate={rotateAnimation}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                }}
              >
                <Box
                  sx={{
                    width: "8px",
                    height: "8px",
                    backgroundColor: theme.palette.secondary.main,
                    borderRadius: "50%",
                    position: "absolute",
                    top: "-10px",
                    left: "50%",
                    transform: "translateX(-50%)",
                  }}
                />
              </motion.div>

              <motion.div
                animate={{
                  scale: [1, 1.2, 1],
                  rotate: [0, -15, 15, -10, 10, 0],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  repeatDelay: 2,
                }}
              >
                <SadIcon
                  sx={{
                    fontSize: { xs: "60px", md: "80px" },
                    color: theme.palette.warning.main,
                    filter: `drop-shadow(0 0 20px ${alpha(theme.palette.warning.main, 0.5)})`,
                  }}
                />
              </motion.div>
            </Box>

            {/* Error message */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <Typography
                variant="h3"
                sx={{
                  // color: theme.palette.text.primary,
                  fontWeight: "bold",
                  mb: 1,
                  background: `linear-gradient(135deg, ${theme.palette.text.primary}, ${theme.palette.secondary.light})`,
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  color: "transparent",
                }}
              >
                Lost in Space?
              </Typography>

              <Typography
                variant="h6"
                sx={{
                  color: alpha(theme.palette.text.secondary, 0.8),
                  maxWidth: "500px",
                  mt: 2,
                }}
              >
                The page you're looking for doesn't exist or has been moved.
                Maybe it's floating somewhere in the digital universe.
              </Typography>
            </motion.div>

            {/* Buttons */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{ mt: 2 }}
            >
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button
                  component={Link}
                  to="/"
                  variant="contained"
                  size="large"
                  startIcon={<HomeIcon />}
                  sx={{
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    borderRadius: "50px",
                    px: 4,
                    py: 1.5,
                    fontWeight: "bold",
                    textTransform: "none",
                    fontSize: "1rem",
                    boxShadow: `0 0 20px ${alpha(theme.palette.primary.main, 0.5)}`,
                    "&:hover": {
                      boxShadow: `0 0 30px ${alpha(theme.palette.primary.main, 0.8)}`,
                      background: `linear-gradient(45deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
                    },
                  }}
                >
                  Back to Home
                </Button>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button
                  component={Link}
                  to="/projects"
                  variant="outlined"
                  size="large"
                  startIcon={<ExploreIcon />}
                  sx={{
                    borderRadius: "50px",
                    px: 4,
                    py: 1.5,
                    fontWeight: "bold",
                    textTransform: "none",
                    fontSize: "1rem",
                    color: theme.palette.secondary.main,
                    borderColor: alpha(theme.palette.secondary.main, 0.5),
                    "&:hover": {
                      borderColor: theme.palette.secondary.main,
                      backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                      color: theme.palette.secondary.light,
                    },
                  }}
                >
                  Explore Projects
                </Button>
              </motion.div>
            </Stack>

            {/* Fun fact with pulse animation */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
              <motion.div
                animate={{
                  opacity: [0.4, 1, 0.4],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: alpha(theme.palette.text.disabled, 0.6),
                    mt: 4,
                    display: "block",
                    fontStyle: "italic",
                  }}
                >
                  💡 Fun fact: 404 is more famous than most successful pages
                </Typography>
              </motion.div>
            </motion.div>
          </Stack>
        </motion.div>
      </Container>
    </Box>
  );
}
